import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { V2AuthSurface, V2FormField, V2Button, Alert } from '@sakhtar/design-system/react';
import { ApiError } from '../../lib/apiClient.js';
import { changePassword, hasPendingPasswordChange } from '../../lib/auth.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { AUTH_BRAND_COPY, AUTH_HIGHLIGHTS, formatOrganizationDisplayLabel } from './authBrand.js';
import logoUrl from '@sakhtar/design-system/assets/logo/sakhtar-white-logo.svg';

const MIN_PASSWORD_LENGTH = 8;

/**
 * UI_REFERENCE_SOURCE:
 * section: no Golden-A specimen exists for this screen (it postdates the
 *   original golden set — D-020/ERP-245/246); built as a new member of the
 *   same split-auth family as Login and Invite Accept, same brand panel and
 *   `V2AuthSurface`/`V2FormField`/`V2Button` components, rather than an
 *   ad-hoc form.
 * state reference: form · submitting · error (validation, expired/used
 *   session — collapsed per ADR-001 §2.3, same convention as Login/OTP)
 *
 * ERP-245/246 — a tenant user invited by mobile/SMS (D-020) never chooses
 * their own password up front: the invite SMS carries a system-generated
 * temporary one, and `POST .../api/auth/session` (LoginPage) already
 * detects it and redirects here (`mustChangePassword`/`redirectTo`) with a
 * restricted `password_change`-scoped session that can reach nothing else.
 * This is that "section after OTP" — the one and only place a tenant user
 * ever types a password of their own choosing, and it only ever follows a
 * successful login with the one-time credential, never precedes it.
 */
export function SetPasswordPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);
  // Stable per page-load so a retry of the same submission reuses the same
  // key rather than a fresh one each attempt (AGENTS.md §9 — sensitive
  // mutations require idempotency; D-020-6 makes a repeat of this exact
  // request a safe replay, not a second consumption of the credential).
  const idempotencyKey = useRef(crypto.randomUUID());

  useEffect(() => {
    // A page reached without a `password_change` session in this tab (a
    // stale reload, a direct link) has nothing to submit against — the only
    // way back is a fresh login, which re-issues the redirect and a fresh
    // proof if the credential is still temporary.
    if (!hasPendingPasswordChange()) {
      setExpired(true);
    }
    // Checked once on mount only — this does not become true later.
  }, []);

  if (!slug) return null;

  const brandProps = {
    logo: <img src={logoUrl} alt="ساختار سیستم" height={28} />,
    brandHeadline: AUTH_BRAND_COPY.brandHeadline,
    brandTagline: AUTH_BRAND_COPY.brandTagline,
    highlights: AUTH_HIGHLIGHTS,
    brandFooter: AUTH_BRAND_COPY.brandFooter,
    tenantLabel: formatOrganizationDisplayLabel(slug),
  };

  if (expired) {
    return (
      <V2AuthSurface {...brandProps} title="نشست معتبر نیست" subtitle="">
        <Alert tone="warning">
          این نشست معتبر نیست یا قبلاً استفاده شده است. لطفاً دوباره با رمز عبور موقتی که برایتان
          پیامک شده وارد شوید.
        </Alert>
        <V2Button
          type="button"
          variant="primary"
          onClick={() => navigate(`${tenantBasePath(slug)}/login`)}
          style={{ width: '100%', marginTop: 16 }}
        >
          بازگشت به صفحهٔ ورود
        </V2Button>
      </V2AuthSurface>
    );
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setError(`رمز عبور باید حداقل ${MIN_PASSWORD_LENGTH} نویسه باشد.`);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('تکرار رمز عبور با رمز عبور وارد شده یکسان نیست.');
      return;
    }
    setSubmitting(true);
    try {
      const result = await changePassword(slug!, newPassword, idempotencyKey.current);
      navigate(
        result.status === 'authenticated' ? result.redirectTo : `${tenantBasePath(slug!)}/admin`,
      );
    } catch (err) {
      if (err instanceof ApiError && err.code === 'VALIDATION_FAILED') {
        setError(`رمز عبور باید حداقل ${MIN_PASSWORD_LENGTH} نویسه باشد.`);
      } else if (err instanceof ApiError && err.code === 'NOT_FOUND') {
        setExpired(true);
      } else if (err instanceof ApiError && err.code === 'RATE_LIMITED') {
        setError('تعداد تلاش‌ها زیاد بوده — کمی بعد دوباره امتحان کنید.');
      } else {
        setError('مشکلی در ارتباط با سرور پیش آمد. دوباره تلاش کنید.');
      }
      setSubmitting(false);
    }
  }

  return (
    <V2AuthSurface
      {...brandProps}
      title="تعیین رمز عبور"
      subtitle="برای امنیت حساب خود، رمز عبور موقتی که برایتان پیامک شد را با یک رمز عبور دائمی جایگزین کنید"
    >
      <form
        onSubmit={onSubmit}
        noValidate
        style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
      >
        <V2FormField
          label="رمز عبور جدید"
          required
          width="full"
          hint={`حداقل ${MIN_PASSWORD_LENGTH} نویسه`}
          htmlFor="new-password"
        >
          <input
            id="new-password"
            className="sk-input"
            type="password"
            autoComplete="new-password"
            minLength={MIN_PASSWORD_LENGTH}
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            required
          />
        </V2FormField>
        <V2FormField label="تکرار رمز عبور" required width="full" htmlFor="confirm-password">
          <input
            id="confirm-password"
            className="sk-input"
            type="password"
            autoComplete="new-password"
            minLength={MIN_PASSWORD_LENGTH}
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            required
          />
        </V2FormField>
        {error ? (
          <Alert tone="danger" title="خطا">
            {error}
          </Alert>
        ) : null}
        <V2Button type="submit" variant="primary" disabled={submitting} style={{ width: '100%' }}>
          {submitting ? 'در حال ذخیره…' : 'تعیین رمز عبور'}
        </V2Button>
      </form>
    </V2AuthSurface>
  );
}
